/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import appContext from '../../constants/appContext';
import Icon from '../Icon';
import PopupMenu from '../PopupMenu';
import { deleteNotify } from '../../api/api';
import Edit from '../../svg/icons/ui-edit.svg';
import Trash from '../../svg/icons/ui-delete.svg';
import CalendarIcon from '../../svg/icons/ic_calendar-solid_20x20.svg';
import PeopleIcon from '../../svg/icons/ic_guests_20x20.svg';
import TicketIcon from '../../svg/icons/ic_ticket_20x20.svg';
import ReservationSummaryImage from '../ReservationSummary/ReservationSummaryImage';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import './AccountNotifyCard.scss';
import { setSignal } from '../../reducers/signalSlice';

const EVENT_SERVICE_TYPE_IDS = [6, 7, 8];
const isEventCheck = (serviceTypeId) => EVENT_SERVICE_TYPE_IDS.includes(serviceTypeId);

const AccountNotifyCard = ({
  $rootScope,
  $route,
  activeNotifications,
  context,
  image,
  locationSlug,
  setSignal,
  toggleWidgetNotifyModal,
  venueId,
  venueCityCode,
  venueEvents,
  venueName,
  venueUrl,
}) => {
  const findEventName = (serviceTypeId, date) => {
    const matchingEvent = venueEvents.find((event) => event.service_type_id === serviceTypeId && event.date === date);
    return matchingEvent?.name;
  };

  const getTimeRange = (notify) => `${moment(`${notify.day} ${notify.time_preferred_start}`).format('llll')} - ${moment(notify.time_preferred_end, 'HH:mm').format('LT')}`;

  const handleDeleteNotifyClick = async (notify) => {
    await deleteNotify(notify);
    window.location.reload();
  };

  const handleClick = (notify) => {
    const eventName = findEventName(activeNotifications[0]?.service_type_id, activeNotifications[0]?.day);

    const {
      day,
      party_size: partySize,
      service_type_id: serviceTypeId,
    } = notify;

    const activeNotificationsForServiceId = activeNotifications.find((service) => service.service_type_id === serviceTypeId);
    const notifyPayload = {
      activeNotifications: {
        [serviceTypeId]: {
          ...activeNotificationsForServiceId,
          end: activeNotificationsForServiceId.time_preferred_end,
          start: activeNotificationsForServiceId.time_preferred_start,
        },
      },
      locationSlug,
      notifyService: serviceTypeId,
      selectedDate: day,
      selectedQuantity: partySize,
      serviceTypeId,
      venueCityCode,
      venueId,
      venueName,
      venueUrl,
      eventName,
      $route,
    };

    if (context === appContext.WIDGET) {
      toggleWidgetNotifyModal(notifyPayload);
      // As Angular.js Widget migration occurs, we need to emit a signal from both
      // Angular $broadcast and Redux setSignal for this shared component.
      $rootScope.$broadcast('TOGGLE_MODAL', notifyPayload);
      setSignal({ name: 'TOGGLE_MODAL', args: notifyPayload });
    } else {
      $rootScope.$broadcast('MODAL', 'notify', notifyPayload);
    }
  };

  const renderSummary = (notify) => {
    const isEvent = isEventCheck(notify.service_type_id);
    return (
      <>
        <div className="AccountNotifyCard__details-row">
          <Icon component={CalendarIcon} name="calendar" />
          {getTimeRange(notify)}
        </div>
        <div className="AccountNotifyCard__details-row">
          {isEvent ? (
            <>
              <Icon component={TicketIcon} name="ticket" />
              <TranslationProvider translationKey="number_tickets" translationValues={{ seats: `${notify.party_size}` }}>
                <span>{`${notify.party_size} Tickets`}</span>
              </TranslationProvider>
            </>
          )
            : (
              <>
                <Icon component={PeopleIcon} name="people" />
                <TranslationProvider translationKey="number_guests" translationValues={{ number: `${notify.party_size}` }}>
                  <span className="AccountNotifyCard__details-row--guests">{`${notify.party_size} Guests`}</span>
                </TranslationProvider>
              </>
            )}
        </div>
      </>
    );
  };

  const renderCtas = (notify) => {
    const notifyConfig = {
      venue_id: notify.venue_id,
      day: notify.day,
      num_seats: notify.party_size,
      service_type_id: notify.service_type_id,
    };

    return (
      <PopupMenu
        ariaLabel={getTranslation({
          translationKey: 'notify_modify_menu',
          fallback: 'Notify Modify Menu',
        })}
        buttonsArr={[
          {
            onClick: () => handleClick(notify),
            icon: Edit,
            text: 'Manage',
            translationKey: 'reservation_manage',
            'data-test-id': 'notify_card-button-manage',
          },
          {
            onClick: () => handleDeleteNotifyClick(notifyConfig),
            icon: Trash,
            text: 'Remove',
            translationKey: 'remove',
            iconProps: { fill: '#eb1700' },
            style: { color: '#eb1700' },
            'data-test-id': 'notify_card-button-remove',
          },
        ]}
        height="24px"
        width="24px"
        testId="notify_card-menu"
      />
    );
  };

  const renderEventLabel = () => (
    <div className="AccountNotifyCard__event-label">{venueName}</div>
  );

  const renderMultiple = () => {
    const [firstNotify, ...restOfNotifies] = activeNotifications;

    return (
      <div className="AccountNotifyCard AccountNotifyCard--multiple">
        <div className="AccountNotifyCard--multiple__inner-container">
          <ReservationSummaryImage
            backgroundImage={image}
          />
          <div className="AccountNotifyCard AccountNotifyCard--multiple__cards-container">
            <div className="AccountNotifyCard--multiple__primary-card">
              <div>
                <div className="AccountNotifyCard__venue-name">{venueName}</div>
                {isEventCheck(firstNotify.service_type_id) && (
                  <>
                    {renderEventLabel()}
                    <div className="AccountNotifyCard__venue-name">{findEventName(firstNotify.service_type_id, firstNotify.day)}</div>
                  </>
                )}
                {renderSummary(firstNotify)}
              </div>
              {renderCtas(firstNotify)}
            </div>

            {restOfNotifies.map((notify) => (
              <div className="AccountNotifyCard--multiple__card" key={`${notify.venue_id}-${notify.day}-${notify.service_type_id}`}>
                <div>
                  {isEventCheck(notify.service_type_id) && (
                    <>
                      {renderEventLabel()}
                      <div className="AccountNotifyCard__venue-name">{findEventName(notify.service_type_id, notify.day)}</div>
                    </>
                  )}
                  {renderSummary(notify)}
                </div>
                {renderCtas(notify)}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (activeNotifications.length > 1) {
    return renderMultiple();
  }

  const isEvent = isEventCheck(activeNotifications[0].service_type_id);
  const eventName = findEventName(activeNotifications[0].service_type_id, activeNotifications[0].day);

  return (
    <div className="AccountNotifyCard">
      <div className="AccountNotifyCard__image-and-summary">
        <ReservationSummaryImage
          backgroundImage={image}
        />
        <div className="AccountNotifyCard__summary">
          {isEvent && renderEventLabel()}
          <div className="AccountNotifyCard__venue-name">{isEvent ? eventName : venueName}</div>
          {renderSummary(activeNotifications[0])}
        </div>
      </div>
      {renderCtas(activeNotifications[0])}
    </div>
  );
};

AccountNotifyCard.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  $route: PropTypes.shape({}).isRequired,
  activeNotifications: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string,
      num_seats: PropTypes.number,
      service_type_id: PropTypes.number,
      time_preferred_end: PropTypes.string,
      time_preferred_start: PropTypes.string,
      venue_id: PropTypes.number,
    })
  ).isRequired,
  context: PropTypes.string,
  image: PropTypes.string.isRequired,
  locationSlug: PropTypes.string.isRequired,
  setSignal: PropTypes.func.isRequired,
  toggleWidgetNotifyModal: PropTypes.func,
  venueId: PropTypes.number.isRequired,
  venueCityCode: PropTypes.string.isRequired,
  venueEvents: PropTypes.arrayOf(PropTypes.shape({})),
  venueName: PropTypes.string.isRequired,
  venueUrl: PropTypes.string.isRequired,
};

AccountNotifyCard.defaultProps = {
  context: 'consumer',
  toggleWidgetNotifyModal: /* istanbul ignore next */ () => {},
  venueEvents: [],
};

const mapDispatchToProps = {
  setSignal,
};

AccountNotifyCard.displayName = 'AccountNotifyCard';
export { AccountNotifyCard as UnconnectedAccountNotifyCard };
export default connect(null, mapDispatchToProps)(AccountNotifyCard);
